import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import { hasRole } from "../../../../../models/User";
import { UserContext } from "../../../../../UserProvider";
import { permissions } from "../../../../../constants";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { listAwaitingByStore } from "../../../../../api/stocktakingApi";
import { Store } from "../../../../../models/Store";
import { isOk } from "../../../../../result";
import moment from "moment";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { Inventory } from "@mui/icons-material";
import CloseIcon from "@material-ui/icons/Close";
import DialogActions from "@material-ui/core/DialogActions";
import useStyles from "../styles";
import { CategoryMultiSelect } from "../../../../../components/selects/CategoryMultiSelect";
import * as StocktakingApi from "../../../../../api/stocktakingApi";
import { Alert } from "@material-ui/lab";
import { RunStocktaking } from "./RunStocktaking";
import { getProductConcatName } from "../../../../../models/Stocktaking";
import { ConfirmDialog } from "../../../../../components/dialogs/ConfirmDialog";

import { stop } from "../../../../../api/stocktakingApi";
import { useI18n } from "../../../../../hooks/useI18n";

const daysBack = [0, 1, 7, 14, 20, 30, 60];

export interface StocktakingListProps {
  store: Store;
}

export const StocktakingList = ({ store }: StocktakingListProps) => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useContext(UserContext);
  const classes = useStyles();
  const { t } = useI18n();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openStartStocktakingDialog, setOpenStartStocktakingDialog] =
    useState(false);

  const [stopStocktakingDialog, setStopStocktakingDialog] = useState(false);
  const [stopingStocktakingFailed, setStopingStocktakingFailed] =
    useState(false);
  const [
    stopingStocktakingFailedErrorMessage,
    setStopingStocktakingFailedErrorMessage
  ] = useState("");

  const [state, setState] = useState({
    isLoading: false,
    reload: true,
    showError: false,
    stockTakingList: [] as any,
    createStocktaking: {} as any
  });

  useEffect(() => {
    if (state.reload) {
      setState({ ...state, isLoading: true });
      listAwaitingByStore(store.id).then(result => {
        if (isOk(result)) {
          setState({
            ...state,
            stockTakingList: result.value,
            isLoading: false,
            reload: false
          });
        }
      });
    }
  }, [state.reload]);

  const renderDialogTitle = () => {
    return (
      <MuiDialogTitle
        disableTypography
        style={{
          display: "flex",
          alignItems: "center",
          paddingBottom: mobile ? 0 : 15
        }}
      >
        <Avatar
          style={{
            backgroundColor: theme.palette.primary.main,
            display: "inline-flex",
            marginRight: 10
          }}
        >
          <Inventory />
        </Avatar>
        <Typography variant={"h6"} display={"inline"}>
          Create stocktaking
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={() => {
            setOpenCreateDialog(false);
          }}
        >
          <CloseIcon />
        </IconButton>
      </MuiDialogTitle>
    );
  };

  return hasRole(user, permissions.stores) ? (
    <>
      <MaterialTable
        title=""
        isLoading={state.isLoading}
        columns={[
          {
            title: "Id",
            field: "Id",
            hidden: true
          },
          {
            title: "Barcode",
            field: "product.gtin",
            searchable: true,
            hidden: mobile
          },
          {
            title: "Product",
            field: "product.name",
            searchable: true,
            hidden: mobile,
            render: (row: any) => getProductConcatName(row.product)
          },
          {
            title: "Product",
            field: "product.name",
            searchable: true,
            hidden: !mobile,
            render: (row: any) => (
              <>
                {getProductConcatName(row.product)}
                <br />
                <strong>{row.GTIN}</strong>
              </>
            )
          },
          {
            title: "Category",
            field: "product.category.name",
            searchable: true,
            hidden: mobile
          },
          {
            title: "Created",
            field: "created",
            render: (row: any) => moment(row.created).format("YYYY-MM-DD HH:mm")
          }
        ]}
        data={state.stockTakingList as []}
        actions={[
          {
            icon: "play_circle",
            disabled:
              !state.stockTakingList || state.stockTakingList.length === 0,
            iconProps: {
              style: {
                color:
                  !state.stockTakingList || state.stockTakingList.length === 0
                    ? theme.palette.grey.A100
                    : theme.palette.primary.main,
                fontSize: "2rem"
              }
            },
            tooltip: t("common.actions.startStocktaking"),
            isFreeAction: true,
            onClick: () => {
              setOpenStartStocktakingDialog(true);
            }
          },
          {
            icon: "stop_circle",
            disabled:
              !state.stockTakingList || state.stockTakingList.length === 0,
            iconProps: {
              style: {
                color:
                  !state.stockTakingList || state.stockTakingList.length === 0
                    ? theme.palette.grey.A100
                    : theme.palette.error.main,
                fontSize: "2rem"
              }
            },
            tooltip: t("common.actions.stopStocktaking"),
            isFreeAction: true,
            onClick: () => {
              setStopStocktakingDialog(true);
            }
          },
          {
            icon: "add_circle",
            disabled: state.stockTakingList && state.stockTakingList.length > 0,
            iconProps: {
              style: {
                color:
                  state.stockTakingList && state.stockTakingList.length > 0
                    ? theme.palette.grey.A100
                    : theme.palette.success.dark,
                fontSize: "2rem"
              }
            },
            tooltip: "Create new stocktaking",
            isFreeAction: true,
            onClick: () => {
              setOpenCreateDialog(true);
            }
          }
        ]}
        options={{
          pageSize: 20,
          exportMenu: !mobile
            ? [
                {
                  label: t("common.export.exportPdf"),
                  exportFunc: (cols, data) => ExportPdf(cols, data, `Users`)
                },
                {
                  label: t("common.export.exportCsv"),
                  exportFunc: (cols, data) => ExportCsv(cols, data, `Users`)
                }
              ]
            : [],
          exportAllData: true
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
        components={{
          Toolbar: props => {
            return (
              <div style={{ marginRight: 10 }}>
                <MTableToolbar {...props} />
              </div>
            );
          }
        }}
      />

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"sm"}
        open={openStartStocktakingDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <IconButton
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            zIndex: 1000,
            backgroundColor: theme.palette.primary.main,
            color: "#fff"
          }}
          aria-label="close"
          onClick={() => {
            setOpenStartStocktakingDialog(false);
            setState({ ...state, reload: true });
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          style={
            mobile
              ? { padding: 0, marginTop: 15, overflow: "hidden" }
              : {
                  marginTop: 15,
                  position: "relative",
                  overflow: "hidden",
                  minHeight: 300
                }
          }
        >
          <RunStocktaking
            store={store}
            stocktakingListInit={state.stockTakingList}
          />
        </DialogContent>
      </Dialog>
      {stopStocktakingDialog && stopStocktakingDialog == true && (
        <ConfirmDialog
          titleText={t("common.actions.stopStocktaking")}
          confirmText={"Yes"}
          onCancel={() => {
            setStopStocktakingDialog(false);
          }}
          onConfirm={async () => {
            const result = await stop(store.id);
            if (isOk(result)) {
              setStopStocktakingDialog(false);
              setState({ ...state, isLoading: false, reload: true });
              return true;
            }
            setStopingStocktakingFailed(true);
            setStopingStocktakingFailedErrorMessage(result.details.detail);
            return false;
          }}
        >
          Are you sure you want to stop the stocktaking? This will clear all
          items in the list that have not been processed
          {stopingStocktakingFailed && (
            <Alert severity="error">
              {stopingStocktakingFailedErrorMessage}
            </Alert>
          )}
        </ConfirmDialog>
      )}

      {/*CREATE DIALOG*/}
      <Dialog
        onClose={() => {
          setOpenCreateDialog(false);
        }}
        open={openCreateDialog}
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"sm"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        {renderDialogTitle()}
        {state.isLoading && (
          <div className={classes.spinnerContainer}>
            <CircularProgress />
          </div>
        )}
        <DialogContent>
          <Container>
            <FormControl className={classes.field}>
              <InputLabel id="period-select-label">
                Not processed the last
              </InputLabel>
              <Select
                labelId="period-select-label"
                value={state.createStocktaking.daysBack ?? ""}
                onChange={e => {
                  return setState({
                    ...state,
                    createStocktaking: {
                      ...state.createStocktaking,
                      daysBack: Number(e.target.value)
                    }
                  });
                }}
                autoWidth={true}
              >
                {daysBack.map(value => (
                  <MenuItem key={value} value={value}>
                    {`${value} days`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              className={classes.createDialogCheckbox}
              control={
                <Checkbox
                  checked={state.createStocktaking.allCategories}
                  onChange={e => {
                    return setState({
                      ...state,
                      createStocktaking: {
                        ...state.createStocktaking,
                        allCategories: e.target.checked,
                        categoryIds: []
                      }
                    });
                  }}
                />
              }
              label={t("common.formFields.allCategories")}
            />
            <CategoryMultiSelect
              disabled={state.createStocktaking.allCategories}
              className={classes.field}
              value={
                state.createStocktaking.categoryIds
                  ? state.createStocktaking.categoryIds
                  : []
              }
              onSelected={categories => {
                return setState({
                  ...state,
                  createStocktaking: {
                    ...state.createStocktaking,
                    categoryIds:
                      categories.length > 0 ? categories.map(x => x.id) : []
                  }
                });
              }}
            />
          </Container>
          <DialogActions style={{ padding: 0 }}>
            <Container>
              <Box pr={0} pb={1} mt={2} display={"flex"} justifyContent={"end"}>
                <Button
                  onClick={() => {
                    setState({ ...state, isLoading: true });
                    StocktakingApi.create({
                      ...state.createStocktaking,
                      storeIds: [store.id]
                    }).then(result => {
                      if (!isOk(result)) {
                        return setState({
                          ...state,
                          showError: true,
                          isLoading: false
                        });
                      }
                      setState({ ...state, isLoading: false, reload: true });
                      setOpenCreateDialog(false);
                    });
                  }}
                  color="primary"
                  autoFocus
                  variant={"contained"}
                  disabled={
                    state.isLoading ||
                    state.createStocktaking?.daysBack === "" ||
                    (!state.createStocktaking?.allCategories &&
                      !state.createStocktaking?.categoryIds?.length)
                  }
                >
                  Save
                </Button>
              </Box>
            </Container>
          </DialogActions>
          {state.showError && (
            <Alert severity="error">
              {t("common.messages.failedToCreateStocktaking")}
            </Alert>
          )}
        </DialogContent>
      </Dialog>
    </>
  ) : (
    <></>
  );
};
